// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import * as taxRegimeApi from '../api'

//Tiempo de vida del catálogo de regímenes fiscales en el store (180 minutos)
export const TAX_REGIMES_CACHE_TTL = 180 * 60 * 1000

//El endpoint debe devolver una lista: cualquier otra forma rompería el `.map` del select
const asList = (data) => (Array.isArray(data) ? data : [])

//Redux Toolkit rellena `action.error.message` con este literal cuando el thunk se rechaza
//con `rejectWithValue`, que es justo el caso de una caída de red (timeout, CORS, backend
//abajo): sin respuesta HTTP no hay `error.response.data` que pasarle
const RTK_REJECTED_SENTINEL = 'Rejected'

//`error` guarda lo que dijo el backend y nada más. Un fallo sin respuesta no dice nada, así
//que se guarda null en vez del literal en inglés de Redux Toolkit: quien lo muestre pondrá
//su propio texto. Que la petición falló ya lo dice `status`
const readError = (action) => {
    const message = action.error?.message

    return action.payload ?? (message === RTK_REJECTED_SENTINEL ? null : message) ?? null
}

const isLatest = (state, action) => state.requestId === action.meta.requestId

export const getTaxRegimes = createAsyncThunk('appTaxRegimes/getTaxRegimes', async (_, { rejectWithValue }) => {
    try {
        return {
            data: await taxRegimeApi.fetchTaxRegimes(),
            fetchedAt: Date.now()
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
}, {
    //Evita peticiones duplicadas y reutiliza el caché mientras siga vigente
    condition: (_, { getState }) => {
        const taxRegimes = getState()?.taxRegimes

        if (!taxRegimes) return true
        //Invalidación explícita (enableRequest): pasa aunque haya una petición en vuelo,
        //cuya respuesta se descarta por requestId
        if (taxRegimes.isRequested) return true
        //Ya hay una petición en vuelo: los demás se cuelgan de esa
        if (taxRegimes.status === 'pending') return false
        //Store vacío
        if (!taxRegimes.data?.length) return true

        return Date.now() - (taxRegimes.lastFetch ?? 0) > TAX_REGIMES_CACHE_TTL
    }
})

export const appTaxRegimesSlice = createSlice({
    name: 'appTaxRegimes',
    initialState: {
        data: [],
        isRequested: true,
        // status: idle | pending | fulfilled | rejected
        status: 'idle',
        error: null,
        lastFetch: null,
        requestId: null
    },
    reducers: {
        enableRequest: (state) => {
            state.isRequested = true
        }
    },
    extraReducers: builder => {
        builder.addCase(getTaxRegimes.pending, (state, action) => {
            state.status = 'pending'
            state.error = null
            //Se consume aquí para que una invalidación durante la petición siga vigente
            state.isRequested = false
            state.requestId = action.meta.requestId
        })
        builder.addCase(getTaxRegimes.fulfilled, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'fulfilled'
            state.data = asList(action.payload.data)
            state.lastFetch = action.payload.fetchedAt
            state.error = null
        })
        builder.addCase(getTaxRegimes.rejected, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'rejected'
            state.error = readError(action)
            //Permite reintentar en la siguiente llamada sin esperar al TTL
            state.isRequested = true
        })
    }
})

export const { enableRequest } = appTaxRegimesSlice.actions

export default appTaxRegimesSlice.reducer
